@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")
@file:OptIn(ExperimentalComposeWebApi::class)

package org.example.project.enginecardview

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.put
import org.example.project.dashboard.DashboardTheme
import org.example.project.dashboard.LocalScreenRefreshVersion
import org.example.project.enginecardview.layouts.TOR_CODE_SETTINGS_FILE
import org.example.project.enginecardview.layouts.TOR_UEBERSICHT_TEXT_KEY
import org.example.project.enginecardview.layouts.TorCard
import org.example.project.enginecardview.layouts.TorCardStyles
import org.example.project.enginecardview.layouts.TorSettingsPopup
import org.jetbrains.compose.web.ExperimentalComposeWebApi
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.example.project.service.ZustandManager
import org.example.project.utils.All
import org.example.project.utils.FormStyles
import org.example.project.enginekartei.KarteiApi
import org.example.project.enginetabular.TableStyles
import org.example.project.service.InfrastrukturApi
import org.example.project.service.GlobalManager
import org.example.project.service.UserSettingsApi
import org.example.project.service.YardApi
import org.example.project.settings.AppDropdown
import org.example.project.screens_cus.infraAktiveFelder
import org.example.project.screens_cus.infraCodeFieldOrder

private val TOR_CODE_DEFAULT_FIELDS = setOf("tor")
private val TOR_UEBERSICHT_FILTER_WIDE = 220.px
private val TOR_UEBERSICHT_FILTER_NORMAL = 150.px

@Composable
fun CUSTorUebersicht(art: String = org.example.project.service.currentSaveArt()) {
    Style(TorCardStyles)


    val scope = rememberCoroutineScope()
    val refreshVersion = maxOf(
        LocalScreenRefreshVersion.current,
        GlobalManager.jsonDomainVersion("yard"),
        GlobalManager.jsonDomainVersion("yard-kontrollen")
    )
    var tore by remember { mutableStateOf<List<All>>(emptyList()) }
    var auftraegeState by remember { mutableStateOf<List<All>>(emptyList()) }
    var standorte by remember { mutableStateOf<List<All>>(emptyList()) }
    var selectedStandort by remember { mutableStateOf("") }
    var selectedHalle by remember { mutableStateOf("") }
    var selectedTor by remember { mutableStateOf("") }
    var datenGeladen by remember { mutableStateOf(false) }
    var infra by remember { mutableStateOf<All?>(null) }
    var showTorSettings by remember { mutableStateOf(false) }
    var torCodeFields by remember { mutableStateOf(TOR_CODE_DEFAULT_FIELDS) }
    var torCodeDraftFields by remember { mutableStateOf(TOR_CODE_DEFAULT_FIELDS) }

    suspend fun loadGefilterteTore() {
        if (selectedStandort.isBlank()) {
            tore = emptyList()
            auftraegeState = emptyList()
            datenGeladen = true
            return
        }
        tore = InfrastrukturApi.loadToreFiltered(
            All(
                standort = selectedStandort,
                halle = selectedHalle,
                tor = selectedTor.toIntOrNull()
            )
        ).filter { (it.tor ?: 0) > 0 }
        val belegungsFilter = All(
            standort = selectedStandort,
            tor = selectedTor.toIntOrNull()
        )
        val we = YardApi.loadWeLkwFiltered(belegungsFilter)
        val waLkw = YardApi.loadWaLkwFiltered(belegungsFilter)
        auftraegeState = we + waLkw
        datenGeladen = true
    }

    LaunchedEffect(art, refreshVersion) {
        standorte = KarteiApi.loadStandorte()
        infra = InfrastrukturApi.loadInfra()
        val loadedFields = parseTorCodeFields(UserSettingsApi.loadCurrentUserSettingsFile(TOR_CODE_SETTINGS_FILE))
        torCodeFields = loadedFields
        torCodeDraftFields = loadedFields
        if (datenGeladen && selectedStandort.isNotBlank()) {
            loadGefilterteTore()
        }
    }

    val standortMap = remember(standorte) {
        standorte.associate { it.id to it.standort }
    }
    fun standortLabel(id: String?) = id?.let { standortMap[it] ?: "" } ?: ""

    val selectedStandortData = standorte.firstOrNull { it.id == selectedStandort }
    fun range(von: String, bis: String, pad: Int): List<String> {
        val start = von.toIntOrNull() ?: return emptyList()
        val end = bis.toIntOrNull() ?: start
        return (start..end).map { it.toString().padStart(pad, '0') }
    }
    val hallenOptionen = range(selectedStandortData?.halleVon.orEmpty(), selectedStandortData?.halleBis.orEmpty(), 2)
    val torOptionen = range(selectedStandortData?.toreVon.orEmpty(), selectedStandortData?.toreBis.orEmpty(), 3)
    val aktuelleTore = tore
    val aktiveTorFelder = infraAktiveFelder(infra, "tor").toSet()

    Div({ classes(FormStyles.wrapper) }) {

        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
                flexWrap(FlexWrap.Wrap)
                gap(10.px)
                marginBottom(18.px)
                padding(12.px, 16.px)
            }
        }) {
            Div({ style { width(TOR_UEBERSICHT_FILTER_WIDE) } }) {
                AppDropdown(
                    options = buildList {
                        add(All(key = "", label = "Standort"))
                        addAll(standorte.map { All(key = it.id, label = it.standort.ifBlank { "Standort ${it.id}" }) })
                    },
                    selectedValue = selectedStandort,
                    onChange = {
                        selectedStandort = it
                        selectedHalle = ""
                        selectedTor = ""
                        datenGeladen = false
                        tore = emptyList()
                    }
                )
            }
            CardViewDivider()
            Div({ style { width(TOR_UEBERSICHT_FILTER_NORMAL) } }) {
                AppDropdown(
                    options = buildList {
                        add(All(key = "", label = "Halle"))
                        addAll(hallenOptionen.map { All(key = it, label = it) })
                    },
                    selectedValue = selectedHalle,
                    enabled = selectedStandort.isNotBlank(),
                    onChange = {
                        selectedHalle = it
                        datenGeladen = false
                        tore = emptyList()
                    }
                )
            }
            CardViewDivider()
            Div({ style { width(TOR_UEBERSICHT_FILTER_NORMAL) } }) {
                AppDropdown(
                    options = buildList {
                        add(All(key = "", label = "Tor"))
                        addAll(torOptionen.map { All(key = it, label = it) })
                    },
                    selectedValue = selectedTor,
                    enabled = selectedStandort.isNotBlank(),
                    onChange = {
                        selectedTor = it
                        datenGeladen = false
                        tore = emptyList()
                    }
                )
            }
            CardViewDivider()
            CardViewActionButton("Suchen", "images/suche.svg") {
                scope.launch { loadGefilterteTore() }
            }
            CardViewActionButton("Einstellungen", "images/settings.svg") {
                torCodeDraftFields = torCodeFields
                showTorSettings = !showTorSettings
            }
            CardViewActionButton("Reset", "images/refresh.svg") {
                selectedStandort = ""
                selectedHalle = ""
                selectedTor = ""
                tore = emptyList()
                auftraegeState = emptyList()
                datenGeladen = false
            }
        }

        if (showTorSettings) {
            TorSettingsPopup(
                fields = torCodeDraftFields,
                availableFields = aktiveTorFelder,
                onToggle = { field, checked ->
                    torCodeDraftFields = if (checked) torCodeDraftFields + field else torCodeDraftFields - field
                },
                onClose = { showTorSettings = false },
                onSave = {
                    val next = torCodeDraftFields
                    torCodeFields = next
                    showTorSettings = false
                    scope.launch {
                        UserSettingsApi.saveCurrentUserSettingsFile(TOR_CODE_SETTINGS_FILE, torCodeFieldsJson(next))
                    }
                }
            )
        }

        when {
            !datenGeladen -> Div({
                style {
                    color(DashboardTheme.TEXT_MUTED)
                    textAlign("center")
                    padding(40.px)
                }
            }) { Text("Filter setzen und Laden drücken.") }
            aktuelleTore.isEmpty() -> Div({
                style {
                    color(DashboardTheme.TEXT_MUTED)
                    textAlign("center")
                    padding(40.px)
                }
            }) { Text("Keine passenden Tore gefunden.") }
            else -> Div({
                style {
                    display(DisplayStyle.Grid)
                    gridTemplateColumns("repeat(auto-fit, minmax(360px, 360px))")
                    justifyContent(JustifyContent.Center)
                    gap(18.px)
                    width(100.percent)
                }
            }) {
                aktuelleTore.forEach { tor ->
                    val torCode = torUebersichtTorKey(tor)
                    val torStandort = tor.standort.trim()
                    val torStandortLabel = standortLabel(tor.standort).trim()
                    val belegungen = auftraegeState.filter {
                        it.zustand >= ZustandManager.TOR_ZUGEWIESEN &&
                            it.zustand < ZustandManager.ABGESCHLOSSEN &&
                            torUebersichtMatchesTor(it, tor, torCode, torStandort, torStandortLabel)
                    }

                    TorCard(
                        tor = tor,
                        infra = infra,
                        codeFields = torCodeFields,
                        belegungen = belegungen,
                        onTorTextSave = { text ->
                            scope.launch {
                                val updated = tor.copy(notiz = text)
                                InfrastrukturApi.saveTor(updated)?.let { saved ->
                                    tore = tore.map { if (torUebersichtTorKey(it) == torCode) saved else it }
                                }
                            }
                        }
                    )
                }
            }
        }
    }
}

private fun parseTorCodeFields(raw: String): Set<String> {
    if (raw.isBlank()) return TOR_CODE_DEFAULT_FIELDS
    return runCatching {
        val root = Json.parseToJsonElement(raw).jsonObject
        infraCodeFieldOrder()
            .filter { root[it]?.jsonPrimitive?.booleanOrNull == true }
            .toSet()
    }.getOrDefault(TOR_CODE_DEFAULT_FIELDS)
}

private fun torCodeFieldsJson(fields: Set<String>): String =
    buildJsonObject {
        infraCodeFieldOrder().forEach { field ->
            put(field, field in fields)
        }
    }.toString()

private fun torUebersichtTorKey(tor: All): String =
    tor.torcode.orEmpty().ifBlank {
        listOf(
            tor.standort.padStart(3, '0'),
            tor.halle.padStart(3, '0'),
            (tor.tor ?: 0).toString().padStart(3, '0')
        ).joinToString("-")
    }

private fun torUebersichtMatchesTor(
    entry: All,
    tor: All,
    torCode: String,
    torStandort: String,
    torStandortLabel: String
): Boolean {
    val entryTorcode = entry.torcode.orEmpty()
    if (entryTorcode.isNotBlank()) return entryTorcode == torCode
    return tor.tor != null &&
        entry.tor == tor.tor &&
        torUebersichtMatchesStandort(entry, torStandort, torStandortLabel)
}

private fun torUebersichtMatchesStandort(entry: All, standortId: String, standortLabel: String): Boolean {
    val id = standortId.trim()
    val label = standortLabel.trim()
    if (id.isBlank() && label.isBlank()) return true

    val primaryStandort = entry.standort.trim()
    if (primaryStandort.isNotBlank()) {
        if (id.isNotBlank() && primaryStandort == id) return true
        if (label.isNotBlank() && primaryStandort.equals(label, ignoreCase = true)) return true
        return false
    }

    val candidates = listOf(
        entry.slotStandortId,
        entry.standort
    ).map { it.trim() }.filter { it.isNotBlank() }

    if (candidates.isEmpty()) return false
    if (id.isNotBlank() && candidates.any { it == id }) return true
    if (label.isNotBlank() && candidates.any { it.equals(label, ignoreCase = true) }) return true
    return false
}
